// src/components/RubricsExportTool.js
import React, { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";

/** ---------------- i18n ---------------- */
const MESSAGES = {
    en_US: {
        headings: { title: "Rubrics Export Tool" }, // not rendered in page chrome
        loading: { rubrics: "Loading rubrics…" },
        errors: {
            missingParams: "Missing required params.",
            loadRubrics: "Failed to load rubrics.",
            downloadFailed: "Download failed.",
            prettyFailed: "Failed to render rubric.",
            printFailed: "Could not open the print dialog.",
            importFailed: "Import failed. Please choose a valid JSON file.",
            publishFailed: "Failed to publish rubric(s).",
            noImported: "No imported rubrics to publish.",
        },
        table: {
            select: "Select",
            title: "Title",
            created: "Created",
            modified: "Modified",
            type: "Rubric Type",
            readOnly: "Read-only",
            actions: "Actions",
            noRubrics: "No rubrics found.",
            importedBadge: "Imported",
        },
        filters: {
            searchPlaceholder: "Filter by title or type…",
            showing: "Showing {current} of {total}",
            selectAllFiltered: "Select all (filtered)",
            clearAll: "Clear all",
        },
        buttons: {
            clearList: "Clear",
            refresh: "Refresh",
            downloadJson: "Download Selected (JSON)",
            previewJson: "Download JSON",
            prettyPdf: "Pretty Print (PDF)",
            prettyPdfSelected: "Pretty Print Selected (PDF)",
            import: "Import",
            publish: "Publish to Blackboard",
            publishOne: "Publish",
        },
        toasts: { selectedCount: "{count} selected" },
        pretty: {
            rubric: "Rubric",
            description: "Description",
            criteria: "Criteria",
            levels: "Levels",
            totalPoints: "Total Points",
            totalPercent: "Total",
            points: "points",
            unavailable: "Unavailable",
            pageFooter: "Generated by Rubrics Export Tool",
            rubricType: "Rubric Type",
            goals: "Align with goals",
            weightOfTotal: "% of total mark",
        },
        importMeta: {
            imported: "Imported: {count}",
            lastFile: "File: {name}",
            publishDone: "Published {ok} of {total}.",
        },
    },
};
const fallbackLocale = "en_US";
const getMessages = (loc) => ({ ...MESSAGES[fallbackLocale], ...(MESSAGES[loc] || {}) });

/** ---------------- helpers ---------------- */
const debounce = (fn, ms = 300) => { let t; return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); }; };
const tpl = (s, vars = {}) => String(s).replace(/\{(\w+)\}/g, (_, k) => (vars[k] == null ? "" : String(vars[k])));
const escapeHtml = (s) => String(s ?? "")
    .replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;").replaceAll("'", "&#39;");
const safeNameForFile = (s) => String(s || "rubric").replace(/[^\w.-]+/g, "_").slice(0, 80);
const normLocale = (loc) => String(loc || "en-US").replace("_", "-");
const isFiniteNum = (v) => Number.isFinite(Number(v));

/** small utils */
const getName = (r) => r?.name ?? r?.title ?? r?.label ?? "";
const getDesc = (r) => r?.description?.text ?? r?.description?.plain ?? r?.description ?? r?.descriptionText ?? "";
const getAvailable = (r) => r?.availability?.available ?? r?.availability ?? "";

/** type helpers — Blackboard may send "Percentage", "Numeric", "Points" */
const isPercentageType = (t) => /^percentage$/i.test(String(t || "").trim());
const isPointsType = (t) => !isPercentageType(t); // treat everything else (e.g., "Numeric") as points

/** Date formatting */
const formatDateTime = (iso, localeTag = "en-US") => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString(localeTag, { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
};

/** Hidden-iframe print (avoids popup blockers) */
function printWithHiddenIframe(html, onError) {
    try {
        const iframe = document.createElement("iframe");
        Object.assign(iframe.style, { position: "fixed", right: "0", bottom: "0", width: "0", height: "0", border: "0" });
        iframe.setAttribute("aria-hidden", "true");
        document.body.appendChild(iframe);

        const win = iframe.contentWindow;
        const doc = win.document;
        doc.open(); doc.write(html); doc.close();

        const doPrint = () => {
            try {
                win.focus();
                setTimeout(() => { win.print(); setTimeout(() => document.body.removeChild(iframe), 1200); }, 60);
            } catch { document.body.removeChild(iframe); onError?.(); }
        };
        if (doc.readyState === "complete") doPrint(); else iframe.onload = doPrint;
    } catch { onError?.(); }
}

/** Assign stable local IDs to imported rubrics */
const withLocalIds = (arr) =>
    arr.map((r, i) => (r && typeof r === "object"
        ? { ...r, _localId: r._localId || `local_${Date.now()}_${i}_${Math.random().toString(36).slice(2, 8)}` }
        : r));

/** ---------------- Component ---------------- */
const RubricsExportTool = ({ platformUrl, locale = "en_US" }) => {
    const M = useMemo(() => getMessages(locale), [locale]);
    const localeTag = useMemo(() => normLocale(locale), [locale]);
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);

    const token = queryParams.get("token");
    const courseId = localStorage.getItem("courseId");

    const [rubrics, setRubrics] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [selected, setSelected] = useState({});
    const [query, setQuery] = useState("");
    const [toast, setToast] = useState("");

    // sorting
    const [sort, setSort] = useState({ key: "modified", dir: "desc" });

    // import/publish
    const [importedRubrics, setImportedRubrics] = useState([]); // array of local rubric objects (each has _localId)
    const [lastImportFileName, setLastImportFileName] = useState("");
    const [publishingOne, setPublishingOne] = useState(""); // id being published
    const fileInputRef = useRef(null);

    const apiV1 = useMemo(() => axios.create({
        baseURL: `${platformUrl}/learn/api/public/v1`,
        timeout: 20000,
        headers: { "Content-Type": "application/json", Accept: "application/json", Authorization: `Bearer ${token}` },
    }), [platformUrl, token]);

    /** Paged GET helper */
    const getAllPaged = async (path, baseParams = {}) => {
        const all = []; let offset = 0; const limit = 100;
        for (;;) {
            const params = { ...baseParams, limit, offset };
            const res = await apiV1.get(path, { params });
            const rows = Array.isArray(res?.data?.results) ? res.data.results : [];
            all.push(...rows);
            if (rows.length < limit) break; offset += limit;
        }
        return all;
    };

    /** Fetch rubric detail (expanded) for JSON/PDF */
    const fetchRubricDetail = async (id) => {
        const params = {
            fields: [
                "id","name","title","description","readOnly","rubricType","created","modified","availability",
                "rows","columns","cells",                 // Ultra shape (flat cells)
                "criteria","criterions","levels"          // legacy fallback
            ].join(","),
            expand: "cells,rows,columns",
        };
        const res = await apiV1.get(`/courses/${encodeURIComponent(courseId)}/rubrics/${encodeURIComponent(id)}`, { params });
        return res?.data;
    };

    /** Load rubrics for table (server) */
    const loadRubrics = async () => {
        if (!platformUrl || !token || !courseId) { setError(M.errors.missingParams); return; }
        setLoading(true); setError("");
        try {
            const list = await getAllPaged(`/courses/${encodeURIComponent(courseId)}/rubrics`,
                { fields: "id,title,name,rubricType,readOnly,created,modified" });
            setRubrics(list);
        } catch (e) {
            setError(e?.response?.data?.message || e?.message || M.errors.loadRubrics);
        } finally { setLoading(false); }
    };

    useEffect(() => {
        let cancelled = false;
        (async () => { if (!cancelled) await loadRubrics(); })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiV1, courseId, platformUrl, token]);

    /** Build table rows: imported (local) + server */
    const importedRows = useMemo(() => {
        return importedRubrics.map((r) => ({
            __local: true,
            __rubric: r,
            id: r._localId,           // use local id for selection key
            title: getName(r) || "(untitled)",
            rubricType: r?.rubricType || "",
            readOnly: false,
            created: null,
            modified: null,
        }));
    }, [importedRubrics]);

    const allRows = useMemo(() => [...importedRows, ...rubrics], [importedRows, rubrics]);

    const getRowTitle = (row) => (row.__local ? row.title : getName(row));
    const getRowId = (row) => (row.__local ? row.id : row.id);

    /** Filtering */
    const [qInternal, setQInternal] = useState(query);
    const debouncedSetQuery = useMemo(() => debounce(setQuery, 250), []);
    useEffect(() => { debouncedSetQuery(qInternal); }, [qInternal, debouncedSetQuery]);

    const filtered = useMemo(() => {
        const q = (query || "").trim().toLowerCase();
        if (!q) return allRows;
        return allRows.filter((r) => {
            const n = (getRowTitle(r) || "").toLowerCase();
            const t = String(r?.rubricType || "").toLowerCase();
            return n.includes(q) || t.includes(q);
        });
    }, [allRows, query]);

    /** Sorting */
    const getSortVal = (r, key) => {
        switch (key) {
            case "title":   return (getRowTitle(r) || "").toLowerCase();
            case "created": return new Date(r?.created).getTime() || 0;
            case "modified":return new Date(r?.modified).getTime() || 0;
            case "type":    return String(r?.rubricType || "").toLowerCase();
            case "readOnly":return r?.readOnly ? 1 : 0;
            default:        return (getRowTitle(r) || "").toLowerCase();
        }
    };
    const displayed = useMemo(() => {
        const arr = [...filtered];
        const dir = sort.dir === "asc" ? 1 : -1;
        arr.sort((a, b) => {
            const av = getSortVal(a, sort.key);
            const bv = getSortVal(b, sort.key);
            if (av < bv) return -1 * dir;
            if (av > bv) return  1 * dir;
            // tie-breaker by title asc
            return (getRowTitle(a) || "").localeCompare(getRowTitle(b) || "");
        });
        return arr;
    }, [filtered, sort]);

    const requestSort = (key) =>
        setSort((prev) => (prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
    const ariaSort = (key) => (sort.key === key ? (sort.dir === "asc" ? "ascending" : "descending") : "none");
    const caretFor = (key) => (sort.key !== key ? "↕" : sort.dir === "asc" ? "▲" : "▼");

    /** Selection helpers on *displayed* rows */
    const toggleSelect = (rowId) => setSelected((prev) => ({ ...prev, [rowId]: !prev[rowId] }));
    const selectedIds = useMemo(() => Object.entries(selected).filter(([_, v]) => v).map(([id]) => id), [selected]);
    const anySelected = selectedIds.length > 0;

    const selectAllFiltered = () => {
        setSelected((prev) => { const next = { ...prev }; displayed.forEach((r) => { const id = getRowId(r); if (id) next[id] = true; }); return next; });
        setToast(tpl(M.toasts.selectedCount, { count: displayed.length })); clearToastSoon();
    };
    const clearAll = () => { setSelected({}); setToast(tpl(M.toasts.selectedCount, { count: 0 })); clearToastSoon(); };
    const clearToastSoon = () => { window.clearTimeout(clearToastSoon._t); clearToastSoon._t = window.setTimeout(() => setToast(""), 1800); };

    const allFilteredSelected = displayed.length > 0 && displayed.every((r) => !!selected[getRowId(r)]);
    const someFilteredSelected = displayed.some((r) => !!selected[getRowId(r)]) && !allFilteredSelected;
    const headerCheckRef = useRef(null);
    useEffect(() => { if (headerCheckRef.current) headerCheckRef.current.indeterminate = someFilteredSelected; }, [someFilteredSelected]);

    const toggleSelectAllHeader = () => {
        if (allFilteredSelected) {
            setSelected((prev) => { const next = { ...prev }; displayed.forEach((r) => { const id = getRowId(r); if (id) delete next[id]; }); return next; });
        } else { selectAllFiltered(); }
    };

    /** -------- JSON Download (selected): supports local + server -------- */
    const downloadJson = async () => {
        if (!anySelected) return;
        try {
            // build a map id -> row for quick lookup
            const rowById = new Map(allRows.map((r) => [getRowId(r), r]));
            const results = [];
            for (const id of selectedIds) {
                const row = rowById.get(id);
                if (!row) continue;
                if (row.__local) {
                    results.push(row.__rubric);
                } else {
                    const data = await fetchRubricDetail(row.id);
                    if (data) results.push(data);
                }
            }
            if (!results.length) return;
            const fname = results.length === 1
                ? `${safeNameForFile(getName(results[0]) || "rubric")}.json`
                : "rubrics.json";
            const blob = new Blob([JSON.stringify(results, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = fname; document.body.appendChild(a); a.click();
            document.body.removeChild(a); URL.revokeObjectURL(url);
        } catch (e) { setError(e?.response?.data?.message || e?.message || M.errors.downloadFailed); }
    };

    /** Download one rubric JSON (row-aware) */
    const previewJsonRow = async (row) => {
        try {
            const data = row.__local ? row.__rubric : (await fetchRubricDetail(row.id));
            if (!data) return;
            const fname = `${safeNameForFile(getRowTitle(row) || `rubric-${getRowId(row)}`)}.json`;
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = fname; document.body.appendChild(a); a.click();
            document.body.removeChild(a); URL.revokeObjectURL(url);
        } catch (e) { setError(e?.response?.data?.message || e?.message || M.errors.downloadFailed); }
    };

    /** Pretty Print (row-aware) */
    const prettyPrintRow = async (row) => {
        try {
            const rubric = row.__local ? row.__rubric : (await fetchRubricDetail(row.id));
            const html = buildPrettyRubricHtml(rubric, M, { includeShell: true });
            printWithHiddenIframe(html, () => setError(M.errors.printFailed));
        } catch (e) { setError(e?.response?.data?.message || e?.message || M.errors.prettyFailed); }
    };

    /** Pretty Print (PDF) for selected (multi-page) */
    const prettyPrintSelected = async () => {
        if (!anySelected) return;
        try {
            const rowById = new Map(allRows.map((r) => [getRowId(r), r]));
            const details = [];
            for (const id of selectedIds) {
                const row = rowById.get(id);
                if (!row) continue;
                if (row.__local) details.push(row.__rubric);
                else {
                    const data = await fetchRubricDetail(row.id);
                    if (data) details.push(data);
                }
            }
            if (!details.length) return;
            const sections = details
                .map((rubric, idx) => buildPrettyRubricHtml(rubric, M, { includeShell: false, pageBreak: idx > 0 }))
                .join("");
            const html = wrapPrettyDocument(sections, M);
            printWithHiddenIframe(html, () => setError(M.errors.printFailed));
        } catch (e) { setError(e?.response?.data?.message || e?.message || M.errors.prettyFailed); }
    };

    /** ------------ Clear / Refresh / Import / Publish ------------ */

    const clearList = () => {
        setRubrics([]); // clears server rows; imported rows remain so you can still publish/edit them
        setSelected({});
        setToast(tpl(M.filters.showing, { current: "0", total: String(rubrics.length) }));
        clearToastSoon();
    };

    const openImportDialog = () => fileInputRef.current?.click();

    const handleImportFile = async (ev) => {
        const file = ev.target.files?.[0];
        if (!file) return;
        setLastImportFileName(file.name);
        try {
            const text = await file.text();
            const parsed = JSON.parse(text);
            const arr = Array.isArray(parsed) ? parsed : [parsed];
            const cleaned = arr.filter((r) => r && typeof r === "object" && (r.rubricType || r.criteria || r.rows));
            if (!cleaned.length) throw new Error("Empty or invalid JSON.");
            setImportedRubrics(withLocalIds(cleaned));
        } catch (e) {
            console.error(e);
            setError(M.errors.importFailed);
        } finally {
            ev.target.value = ""; // allow choosing the same file again
        }
    };

    // remove read-only/IDs/private keys to avoid conflicts on create
    const stripIdsDeep = (node) => {
        if (Array.isArray(node)) return node.map(stripIdsDeep);
        if (node && typeof node === "object") {
            const out = {};
            for (const [k, v] of Object.entries(node)) {
                if (k === "id" || k === "created" || k === "modified" || k === "readOnly" || k.startsWith("_")) continue;
                out[k] = stripIdsDeep(v);
            }
            return out;
        }
        return node;
    };

    const publishImported = async () => {
        if (!importedRubrics.length) { setError(M.errors.noImported); return; }
        if (!platformUrl || !token || !courseId) { setError(M.errors.missingParams); return; }
        setError("");
        let ok = 0;
        try {
            for (const r of importedRubrics) {
                const body = stripIdsDeep(r);
                if (!body.title && getName(body)) body.title = getName(body);
                await apiV1.post(`/courses/${encodeURIComponent(courseId)}/rubrics`, body);
                ok += 1;
            }
            setToast(tpl(M.importMeta.publishDone, { ok: String(ok), total: String(importedRubrics.length) }));
            clearToastSoon();
            setImportedRubrics([]);
            await loadRubrics();
        } catch (e) {
            console.error(e);
            setError(e?.response?.data?.message || e?.message || M.errors.publishFailed);
        }
    };

    const publishOneImported = async (row) => {
        if (!row?.__local) return;
        if (!platformUrl || !token || !courseId) { setError(M.errors.missingParams); return; }
        try {
            setPublishingOne(row.id);
            const body = stripIdsDeep(row.__rubric);
            if (!body.title && getRowTitle(row)) body.title = getRowTitle(row);
            await apiV1.post(`/courses/${encodeURIComponent(courseId)}/rubrics`, body);
            // remove it from imported list and reload server list
            setImportedRubrics((prev) => prev.filter((r) => r._localId !== row.id));
            await loadRubrics();
        } catch (e) {
            console.error(e);
            setError(e?.response?.data?.message || e?.message || M.errors.publishFailed);
        } finally {
            setPublishingOne("");
        }
    };

    /** Render */
    return (
        <div className="bb-container" style={{ padding: 24, maxWidth: 1200, margin: "0 auto" }}>
            {/* Styles */}
            <style>{`
        .bb-check-wrap{display:inline-flex;align-items:center;gap:8px}
        .bb-checkbox{
          all:revert;
          -webkit-appearance: checkbox;
          appearance: auto;
          display:inline-block !important;
          width:16px !important;height:16px !important;
          margin:0 !important;border:initial !important;
          position:static !important;opacity:1 !important;visibility:visible !important;
          pointer-events:auto !important;vertical-align:middle;
          accent-color:#111827;
        }
        .bb-row-selected{background:#eef6ff}
        .bb-selected-badge{padding:4px 10px;border:1px solid #c7d2fe;background:#eef2ff;border-radius:999px;font-size:12px;color:#3730a3}
        .bb-topbar{display:flex;align-items:center;gap:8px;flex-wrap:nowrap;overflow-x:auto;white-space:nowrap;margin-bottom:12px}
        .bb-topbar .bb-btn{flex:0 0 auto}
        .bb-sort{background:none;border:none;padding:0;margin:0;cursor:pointer;font:inherit;color:inherit;display:inline-flex;align-items:center;gap:6px}
        .bb-caret{font-size:11px;opacity:.7}
        .bb-import-chip{padding:4px 10px;border:1px solid #86efac;background:#ecfdf5;border-radius:999px;font-size:12px;color:#065f46}
        .bb-file{display:none}
        .bb-badge{display:inline-block;padding:2px 8px;border:1px solid #d1d5db;border-radius:999px;font-size:11px;line-height:1.5;background:#f3f4f6;color:#374151}
      `}</style>

            {/* Top bar: Clear / Refresh / Download / Print / Import / Publish */}
            <div className="bb-topbar">
                <button className="bb-btn" onClick={clearList}>{M.buttons.clearList}</button>
                <button className="bb-btn" onClick={loadRubrics} disabled={loading}>{M.buttons.refresh}</button>
                <button className="bb-btn" onClick={downloadJson} disabled={!anySelected}>{M.buttons.downloadJson}</button>
                <button className="bb-btn" onClick={prettyPrintSelected} disabled={!anySelected}>{M.buttons.prettyPdfSelected}</button>

                <span style={{ width: 1, height: 20, background: "#e5e7eb", display: "inline-block", margin: "0 4px" }} />

                <input
                    ref={fileInputRef}
                    className="bb-file"
                    type="file"
                    accept="application/json"
                    onChange={handleImportFile}
                />
                <button className="bb-btn" onClick={openImportDialog}>{M.buttons.import}</button>
                <button className="bb-btn" onClick={publishImported} disabled={!importedRubrics.length}>{M.buttons.publish}</button>

                {importedRubrics.length > 0 && (
                    <span className="bb-import-chip">
            {tpl(M.importMeta.imported, { count: String(importedRubrics.length) })}
                        {lastImportFileName ? ` • ${tpl(M.importMeta.lastFile, { name: lastImportFileName })}` : ""}
          </span>
                )}
            </div>

            {/* Filter toolbar */}
            <div className="bb-toolbar" style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
                <input className="bb-input" style={{ minWidth: 260 }} placeholder={M.filters.searchPlaceholder} value={qInternal} onChange={(e) => setQInternal(e.target.value)} />
                <div className="bb-meta" style={{ opacity: 0.8 }}>
                    {tpl(M.filters.showing, { current: String(displayed.length), total: String(allRows.length) })}
                </div>
                {selectedIds.length > 0 && <span className="bb-selected-badge">Selected: {selectedIds.length}</span>}
                <div style={{ flex: 1 }} />
                <button className="bb-btn" onClick={selectAllFiltered} disabled={!displayed.length}>{M.filters.selectAllFiltered}</button>
                <button className="bb-btn" onClick={clearAll} disabled={!anySelected}>{M.filters.clearAll}</button>
            </div>

            {error && <div className="bb-alert bb-alert-error" style={{ marginBottom: 12 }}>{error}</div>}
            {toast && <div className="bb-toast" style={{ marginBottom: 12 }}>{toast}</div>}

            {/* Table */}
            <div className="bb-table-wrap">
                {loading ? (
                    <div className="bb-card" style={{ padding: 16 }}>{M.loading.rubrics}</div>
                ) : (
                    <table className="bb-table">
                        <thead>
                        <tr>
                            <th style={{ width: 120 }}>
                                <label className="bb-check-wrap">
                                    <input
                                        ref={headerCheckRef}
                                        className="bb-checkbox"
                                        type="checkbox"
                                        aria-label="Select all (filtered)"
                                        checked={allFilteredSelected}
                                        onChange={toggleSelectAllHeader}
                                    />
                                    <span>{M.table.select}</span>
                                </label>
                            </th>

                            <th style={{ width: "26%" }} aria-sort={ariaSort("title")}>
                                <button className="bb-sort" onClick={() => requestSort("title")}>
                                    {M.table.title} <span className="bb-caret">{caretFor("title")}</span>
                                </button>
                            </th>

                            <th style={{ width: 150 }} aria-sort={ariaSort("created")}>
                                <button className="bb-sort" onClick={() => requestSort("created")}>
                                    {M.table.created} <span className="bb-caret">{caretFor("created")}</span>
                                </button>
                            </th>

                            <th style={{ width: 150 }} aria-sort={ariaSort("modified")}>
                                <button className="bb-sort" onClick={() => requestSort("modified")}>
                                    {M.table.modified} <span className="bb-caret">{caretFor("modified")}</span>
                                </button>
                            </th>

                            <th style={{ width: 140 }} aria-sort={ariaSort("type")}>
                                <button className="bb-sort" onClick={() => requestSort("type")}>
                                    {M.table.type} <span className="bb-caret">{caretFor("type")}</span>
                                </button>
                            </th>

                            <th style={{ width: 110 }} aria-sort={ariaSort("readOnly")}>
                                <button className="bb-sort" onClick={() => requestSort("readOnly")}>
                                    {M.table.readOnly} <span className="bb-caret">{caretFor("readOnly")}</span>
                                </button>
                            </th>

                            <th style={{ width: 360 }}>{M.table.actions}</th>
                        </tr>
                        </thead>
                        <tbody>
                        {displayed.length === 0 ? (
                            <tr><td colSpan={7} style={{ textAlign: "center" }}>{M.table.noRubrics}</td></tr>
                        ) : displayed.map((row) => {
                            const rowId = getRowId(row);
                            return (
                                <tr key={rowId} className={selected[rowId] ? "bb-row-selected" : ""}>
                                    <td>
                                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                            <input
                                                className="bb-checkbox"
                                                type="checkbox"
                                                checked={!!selected[rowId]}
                                                onChange={() => toggleSelect(rowId)}
                                                aria-label={`Select rubric ${getRowTitle(row) || rowId}`}
                                            />
                                            {row.__local && <span className="bb-badge">{M.table.importedBadge}</span>}
                                        </div>
                                    </td>
                                    <td>{getRowTitle(row) || "—"}</td>
                                    <td>{formatDateTime(row?.created, localeTag)}</td>
                                    <td>{formatDateTime(row?.modified, localeTag)}</td>
                                    <td>{row?.rubricType || "—"}</td>
                                    <td>{typeof row?.readOnly === "boolean" ? (row.readOnly ? "Yes" : "No") : "—"}</td>
                                    <td style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                        <button className="bb-btn" onClick={() => previewJsonRow(row)}>{M.buttons.previewJson}</button>
                                        <button className="bb-btn" onClick={() => prettyPrintRow(row)}>{M.buttons.prettyPdf}</button>
                                        {row.__local && (
                                            <button
                                                className="bb-btn"
                                                onClick={() => publishOneImported(row)}
                                                disabled={publishingOne === rowId}
                                                title="Publish this imported rubric to Blackboard"
                                            >
                                                {M.buttons.publishOne}
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

/** --------------- Pretty Print builders --------------- */
function wrapPrettyDocument(sectionsHtml, M) {
    return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(M.headings?.title || "Rubrics Export Tool")}</title>
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <style>
    @page { size: A4 portrait; margin: 14mm; }
    :root { --ink:#1f2937; --muted:#6b7280; --line:#e5e7eb; --bg:#ffffff; --head:#f9fafb; }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; background: var(--bg); color: var(--ink); font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial; }
    .page { padding: 24px 28px; }
    .pagebreak { page-break-before: always; }
    header { display: grid; grid-template-columns: 1fr auto; gap: 12px; align-items: end; margin-bottom: 18px; }
    h1 { margin: 0; font-size: 22px; line-height: 1.25; }
    .desc { margin: 8px 0 16px; color: var(--muted); white-space: pre-wrap; }
    .meta { text-align: right; font-size: 12px; color: var(--muted); }
    .badge { display: inline-block; padding: 4px 10px; border: 1px solid var(--line); border-radius: 999px; margin-left: 6px; }
    .rubric-type { margin: 2px 0 0; font-size: 12px; color: var(--muted); }
    table.grid { width: 100%; border-collapse: separate; border-spacing: 0; table-layout: fixed; }
    .grid th, .grid td { border: 1px solid var(--line); vertical-align: top; }
    .grid thead th { background: var(--head); font-weight: 700; padding: 10px; text-align: left; }
    .crit { width: 28%; }
    .cell { padding: 10px; }
    .lvl-name { font-weight: 700; }
    .lvl-metric { margin-top: 2px; font-size: 12px; color: var(--muted); }
    .cell-metric { font-weight: 700; font-size: 12px; margin-bottom: 4px; }
    .cell-perf { white-space: pre-wrap; color: var(--ink); font-size: 12px; }
    .crit-name { font-weight: 700; margin-bottom: 4px; }
    .crit-desc { font-size: 12px; color: var(--muted); white-space: pre-wrap; }
    .crit-weight { padding: 8px 10px; font-size: 12px; color: var(--ink); background: #f3f4f6; }
    .crit-goals { padding: 8px 10px; font-size: 12px; color: #1d4ed8; text-decoration: underline; }
    .footer { margin-top: 16px; color: var(--muted); font-size: 11px; display: flex; justify-content: space-between; }
    @media print { .page { padding: 0; } }
  </style>
</head>
<body>
  ${sectionsHtml}
</body>
</html>`;
}

/** Normalize Blackboard Ultra (rows/columns + flat cells) and legacy shapes */
function normalizeExpandedGrid(rubric) {
    const hasExpanded = Array.isArray(rubric?.rows) && Array.isArray(rubric?.columns);
    if (!hasExpanded) {
        // Legacy fallback (criteria/levels/rubricCriterionLevels)
        const levels = (Array.isArray(rubric?.levels) ? rubric.levels : []).map((l, i) => ({
            name: l?.name || l?.title || `Level ${i + 1}`,
            points: isFiniteNum(l?.points) ? Number(l.points) : null,
            percent: isFiniteNum(l?.percent) ? Number(l.percent) : null,
            description: l?.description ?? "",
        }));
        const criteria = (Array.isArray(rubric?.criteria) ? rubric.criteria
            : Array.isArray(rubric?.criterions) ? rubric.criterions : []).map((c, j) => ({
            name: c?.name || c?.title || `Criterion ${j + 1}`,
            description: c?.description ?? "",
            weight: guessRowWeight(c),
            goals: extractGoals(c),
            rubricCriterionLevels: Array.isArray(c?.rubricCriterionLevels) ? c.rubricCriterionLevels : [],
        }));
        return { levels, criteria, mode: "fallback" };
    }

    // Ultra structure: columns[].header, rows[].header + rows[].percentage, and cells[] at root
    const rType = String(rubric?.rubricType || "").trim();

    const levels = [...rubric.columns]
        .sort((a, b) => (a?.position ?? 0) - (b?.position ?? 0))
        .map((col, i) => ({
            name: col?.header || col?.name || col?.title || `Level ${i + 1}`,
            points: isPointsType(rType) ? null : null,
            percent: isPercentageType(rType) ? null : null,
            description: col?.description ?? "",
        }));

    // Index cells by rowId (ordered by columnPosition)
    const rootCells = Array.isArray(rubric?.cells) ? rubric.cells : [];
    const cellsByRow = new Map();
    for (const cell of rootCells) {
        if (!cell?.rowId) continue;
        if (!cellsByRow.has(cell.rowId)) cellsByRow.set(cell.rowId, []);
        cellsByRow.get(cell.rowId).push(cell);
    }
    for (const arr of cellsByRow.values()) arr.sort((a, b) => (a?.columnPosition ?? 0) - (b?.columnPosition ?? 0));

    const criteria = [...rubric.rows]
        .sort((a, b) => (a?.position ?? 0) - (b?.position ?? 0))
        .map((row, j) => {
            const rowCells = cellsByRow.get(row.id) || [];
            const rubricCriterionLevels = rowCells.map((cell) => ({
                percent: isFiniteNum(cell?.percentage?.value) ? Number(cell.percentage.value) : null,
                points: isFiniteNum(cell?.points?.value) ? Number(cell.points.value)
                    : (isFiniteNum(cell?.points) ? Number(cell.points) : null),
                performance: cell?.description ?? "",
            }));
            return {
                name: row?.header || row?.name || row?.title || `Criterion ${j + 1}`,
                description: row?.description ?? "",
                weight: extractRowWeight(row), // rows[].percentage in Ultra payload
                goals: extractGoals(row),
                rubricCriterionLevels,
            };
        });

    return { levels, criteria, mode: "ultra-flat" };
}

/** Row weight: Ultra payload uses rows[].percentage */
function extractRowWeight(row) {
    if (isFiniteNum(row?.percentage)) return Math.max(0, Math.min(100, Number(row.percentage)));
    const cands = [row?.weightPercent, row?.percentageOfTotal, row?.percent, row?.weighting, row?.weight]
        .map((v) => (isFiniteNum(v) ? Number(v) : null)).filter((v) => v != null);
    if (cands.length) return Math.max(0, Math.min(100, cands[0]));
    if (typeof row?.weightText === "string") {
        const m = row.weightText.match(/(\d+(?:\.\d+)?)\s*%/);
        if (m) return Math.max(0, Math.min(100, Number(m[1])));
    }
    return null;
}

/** Goals/alignments (kept for completeness) */
function extractGoals(node) {
    const arr = node?.goals || node?.alignments || node?.objectives || [];
    if (!Array.isArray(arr) || arr.length === 0) return [];
    return arr.map((g, i) => ({ title: g?.title || g?.name || g?.label || `Goal ${i + 1}`, url: g?.url || g?.link || null }));
}

/** For legacy fallback */
function guessRowWeight(c) {
    const candidates = [c?.weightPercent, c?.percent, c?.weight, c?.percentageOfTotal]
        .map((v) => (isFiniteNum(v) ? Number(v) : null)).filter((v) => v != null);
    return candidates.length ? Math.max(0, Math.min(100, candidates[0])) : null;
}

/** Format a points or percent number for display in headers/cells */
function formatMetric({ type, points, percent }) {
    if (isPercentageType(type)) {
        if (isFiniteNum(percent)) return `${Number(percent)}%`;
        if (isFiniteNum(points)) return `${Number(points)}%`;
        return "";
    }
    if (isFiniteNum(points)) return `${Number(points)}`;
    return "";
}

/** Totals:
 * - Percentage rubric: total is always 100% (display only)
 * - Points rubric: sum of per-criterion max points (max over its cells)
 */
function computeTotals({ rubricType, levels, criteria }) {
    if (isPercentageType(rubricType)) return { totalPoints: null };
    let sum = 0; let any = false;
    for (const c of criteria) {
        const pts = (c?.rubricCriterionLevels || [])
            .map((cell) => (isFiniteNum(cell?.points) ? Number(cell.points) : null))
            .filter((v) => v != null);
        if (pts.length) { any = true; sum += Math.max(...pts); }
    }
    return { totalPoints: any ? sum : null };
}

/** Pretty builder */
function buildPrettyRubricHtml(rubric, M, { includeShell = true, pageBreak = false } = {}) {
    const name = escapeHtml(getName(rubric) || "");
    const desc = escapeHtml(getDesc(rubric));
    const available = getAvailable(rubric) !== "" ? String(getAvailable(rubric)) : M.pretty.unavailable;
    const rubricType = String(rubric?.rubricType || "").trim() || "Points";

    const { levels, criteria } = normalizeExpandedGrid(rubric);
    const totals = computeTotals({ rubricType, levels, criteria });

    const levelHeaders = levels.map((lvl, idx) => {
        const metric = formatMetric({ type: rubricType, points: lvl.points, percent: lvl.percent });
        return `<th class="cell">
      <div class="lvl-name">${escapeHtml(lvl?.name || `Level ${idx + 1}`)}</div>
      ${metric ? `<div class="lvl-metric">${escapeHtml(metric)}</div>` : ""}
    </th>`;
    }).join("");

    const rowsHtml = criteria.map((c, cIdx) => {
        const criterionName = escapeHtml(c?.name || `Criterion ${cIdx + 1}`);
        const criterionDesc = escapeHtml(c?.description || "");
        const weightLine = isFiniteNum(c?.weight)
            ? `<tr><td class="crit-weight" colspan="${1 + levels.length}">
            ${escapeHtml(String(c.weight))}${escapeHtml(M.pretty.weightOfTotal)}
         </td></tr>`
            : "";

        const goalsLine = Array.isArray(c?.goals) && c.goals.length
            ? `<tr><td class="crit-goals" colspan="${1 + levels.length}">
           ${escapeHtml(M.pretty.goals)} ${
                c.goals.map(g => g?.url
                    ? `<a href="${escapeHtml(g.url)}" target="_blank" rel="noopener">${escapeHtml(g.title)}</a>`
                    : escapeHtml(g.title)
                ).join(", ")
            }
         </td></tr>`
            : "";

        const cells = levels.map((lvl, lIdx) => {
            const cell = (c?.rubricCriterionLevels || [])[lIdx] || {};
            const metric = formatMetric({ type: rubricType, points: cell?.points, percent: cell?.percent });
            const perf = cell?.performance ?? lvl?.description ?? "";
            return `<td class="cell">
        ${metric ? `<div class="cell-metric">${escapeHtml(metric)}</div>` : ""}
        <div class="cell-perf">${escapeHtml(perf || "")}</div>
      </td>`;
        }).join("");

        return `
      <tr>
        <th class="cell crit">
          <div class="crit-name">${criterionName}</div>
          ${criterionDesc ? `<div class="crit-desc">${escapeHtml(criterionDesc)}</div>` : ""}
        </th>
        ${cells}
      </tr>
      ${weightLine}
      ${goalsLine}`;
    }).join("");

    const section = `
  <div class="page ${pageBreak ? "pagebreak" : ""}">
    <header>
      <div>
        <h1>${name || escapeHtml(M.pretty.rubric)}</h1>
        <div class="rubric-type"><strong>${escapeHtml(M.pretty.rubricType)}:</strong> ${escapeHtml(rubricType)}</div>
        ${desc ? `<div class="desc">${desc}</div>` : ""}
      </div>
      <div class="meta">
        <span class="badge">${escapeHtml(M.pretty.levels)}: ${levels.length || 0}</span>
        <span class="badge">${escapeHtml(M.pretty.criteria)}: ${criteria.length || 0}</span>
        ${isPercentageType(rubricType)
        ? `<span class="badge">${escapeHtml(M.pretty.totalPercent)}: 100%</span>`
        : (totals.totalPoints != null ? `<span class="badge">${escapeHtml(M.pretty.totalPoints)}: ${totals.totalPoints}</span>` : "")
    }
        <span class="badge">${escapeHtml("Available")}: ${escapeHtml(String(available))}</span>
      </div>
    </header>

    <table class="grid">
      <thead><tr><th class="cell crit">${escapeHtml(M.pretty.criteria)}</th>${levelHeaders}</tr></thead>
      <tbody>${rowsHtml}</tbody>
    </table>

    <div class="footer">
      <div>${escapeHtml(M.pretty.pageFooter)}</div>
      <div>${new Date().toLocaleString()}</div>
    </div>
  </div>`;

    if (!includeShell) return section;
    return wrapPrettyDocument(section, M);
}

export default RubricsExportTool;
